import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Currency,
  Pagination,
  Response,
  Signature,
  TransactionWarning,
  TransactionWarningActionEnum,
  TransactionWarningTypeEnum,
} from '@ui/src';
import { cashCollectionListApi } from '../services';
import ROUTES from '../../../../routes';
import {
  Order,
  PaymentOrder,
  PaymentOrderStatuses,
  WarningGroup,
} from '../../types';
import { WarningsInfo } from '../../../transactions/type';

export interface CashCollectionListState {
  transactions?: PaymentOrder[];
  pagination?: Pagination;
  takeCount?: number;
  transaction?: PaymentOrder;
  signatures?: Signature[];
  statuses?: PaymentOrderStatuses;
  orders?: Order[];
  warnings?: WarningGroup[];
  cashCollectionOrders?: Order[];
  cashCollectionOrdersPagination?: Pagination;
  cashCollectionOrder?: Order;
  warningsInfo?: WarningsInfo;
}

const initialState: CashCollectionListState = {
  transactions: undefined,
};

export const cashCollectionListSlice = createSlice({
  name: 'cashCollectionList',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    clearPaymentOrdersList: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
    },
    clearCashCollectionOrder: (state) => {
      state.cashCollectionOrder = undefined;
    },
    clearPaymentOrdersBeneficiariesList: (state) => {
      state.cashCollectionOrders = undefined;
      state.cashCollectionOrdersPagination = undefined;
      state.takeCount = undefined;
    },
    clearPaymentOrderState: (state) => {
      state.transaction = undefined;
      state.signatures = undefined;
      state.orders = undefined;
      state.warnings = undefined;
      state.statuses = undefined;
      state.warningsInfo = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrders.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentOrder>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          orderCreated: item.node.orderCreatedAt,
          url: generatePath(ROUTES.CASH_COLLECTION_TRANSACTION, {
            transactionId: item.node.id,
          }),
        }));

        state.transactions = nextTransactions;

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.createPaymentOrder.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        let nextTransactions: PaymentOrder[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as PaymentOrder[]).concat(state.transactions);
        }

        nextTransactions.unshift({
          ...action.payload.data,
          orderCreated: action.payload.data.orderCreatedAt,
          url: generatePath(ROUTES.CASH_COLLECTION_TRANSACTION, {
            transactionId: action.payload.data.id,
          }),
        });

        state.transactions = nextTransactions;

        if (state.takeCount) {
          state.takeCount += 1;
        }
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderSignatures.matchFulfilled,
      (state, action: PayloadAction<Response<Signature>>) => {
        state.signatures = action.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrder.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.transaction = action.payload.data;

        if (
          (action.payload.data.status === 'ACCEPTED' ||
            action.payload.data.status === 'REJECTED') &&
          state.statuses
        ) {
          state.statuses = undefined;
        }
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentOrderWarningsInfo
        .matchFulfilled,
      (state, action: PayloadAction<{ data: WarningsInfo }>) => {
        state.warningsInfo = action.payload.data;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderExtended.matchFulfilled,
      (state, action: PayloadAction<{ data: { beneficiaries: Order[] } }>) => {
        state.orders = action.payload.data.beneficiaries;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderWarningsWithPagination
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<TransactionWarning>> & {
          meta: {
            arg: {
              originalArgs: {
                warningGroupId: string;
              };
            };
          };
        },
      ) => {
        if (state.warnings) {
          const nextWarningsGroups = state.warnings?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningGroupId) {
              const warningResult = { ...result.warnings };

              warningResult.pageInfo = action.payload.data.pageInfo;

              const currentWarnings = result.warnings.edges?.map(
                (warning) => warning.node,
              );

              const nextWarnings = ([] as TransactionWarning[]).concat(
                currentWarnings,
              );

              action?.payload?.data?.edges?.forEach((warning) =>
                nextWarnings.push(warning.node),
              );

              warningResult.edges = nextWarnings?.map((warning) => ({
                node: warning,
              }));

              result.warnings = warningResult;
            }

            return result;
          });

          state.warnings = nextWarningsGroups;
        }
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getWarningGroupBeneficiaries
        .matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            senderName?: string;
            localAmount: number;
            localCurrency: Currency;
            destAmount: number;
            destCurrency: Currency;
            createdAt: string;
            paymentOrderId?: string;
            paymentOrder?: {
              recepientAccount?: {
                organization: {
                  id: string;
                  companyName?: string;
                  type?: string;
                  firstName?: string;
                  lastName?: string;
                };
              };
            };
          }>
        > & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        const nextWarningsGroups = state.warnings?.map((item) => {
          const result = { ...item };

          if (item.id === action.meta.arg.originalArgs.id) {
            const nextWarnings = item?.warnings?.edges?.map((warning) => {
              let warningResult = { ...warning };

              if (
                warningResult.node.id === action.meta.arg.originalArgs.warningId
              ) {
                const nextBeneficiaries = ([] as any[]).concat(
                  warningResult?.node?.warningsToPaymentOrderBeneficiaries
                    ?.edges,
                );

                action?.payload?.data?.edges?.forEach((beneficiary) => {
                  nextBeneficiaries.push({
                    node: beneficiary.node,
                  });
                });

                warningResult = {
                  node: {
                    ...warningResult.node,
                    warningsToPaymentOrderBeneficiaries: {
                      edges: nextBeneficiaries,
                      pageInfo: action.payload.data.pageInfo,
                    },
                  },
                };
              }

              return warningResult;
            });

            result.warnings = {
              edges: nextWarnings,
              pageInfo: result.warnings.pageInfo,
            };
          }

          return result;
        });

        state.warnings = nextWarningsGroups;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderStatuses.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrderStatuses }>) => {
        state.statuses = action.payload.data;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderWarnings.matchFulfilled,
      (state, action: PayloadAction<Response<WarningGroup>>) => {
        state.warnings = action.payload.data?.edges?.map((item) => ({
          ...item.node,
          type: item.node?.warnings?.edges?.length
            ? item.node?.warnings?.edges[0].node.type
            : undefined,
        }));
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrderWarning.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TransactionWarning }> & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        const nextWarnings = state.warnings?.map((item) => {
          const result = { ...item };

          if (item.id === action.meta.arg.originalArgs.id) {
            const nextGroupWarnings = item.warnings?.edges?.map((warning) => {
              const warningResult = { ...warning };

              if (
                warningResult.node.id === action.meta.arg.originalArgs.warningId
              ) {
                if (
                  action.payload.data.type ===
                  TransactionWarningTypeEnum.BLACKLIST
                ) {
                  warningResult.node.blacklistDoc =
                    action.payload.data.blacklistDoc;
                  warningResult.node.blacklistItem =
                    action.payload.data.blacklistItem;
                }

                if (
                  action.payload.data.type ===
                  TransactionWarningTypeEnum.MONITORING
                ) {
                  warningResult.node.relatedTransactions =
                    action.payload.data.relatedTransactions;
                }
              }

              return warningResult;
            });

            result.warnings = {
              edges: nextGroupWarnings,
              pageInfo: result.warnings.pageInfo,
            };
          }

          return result;
        });

        state.warnings = nextWarnings;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.updatePaymentOrder.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.transaction = {
          ...state.transaction,
          ...action.payload.data,
        };

        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                orderCreated: action.payload.data?.orderCreatedAt,
                url: generatePath(ROUTES.CASH_COLLECTION_TRANSACTION, {
                  transactionId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.updatePaymentOrderStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.transaction = {
          ...state.transaction,
          ...action.payload.data,
        };

        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = {
                ...action.payload.data,
                orderCreated: action.payload.data?.orderCreatedAt,
                url: generatePath(ROUTES.CASH_COLLECTION_TRANSACTION, {
                  transactionId: action.payload.data.id,
                }),
              };
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.applyWarningAction.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.warnings?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = { node: action.payload.data };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.warnings = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.resetPaymentOrderTransactionWarningAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.warnings?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = {
                node: {
                  ...action.payload.data,
                  action: undefined,
                  matchStatus: undefined,
                  comment: undefined,
                },
              };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.warnings = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.batchApplyWarningAction.matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  action: string;
                  comment: string;
                  matchStatus?: string;
                };
              };
            };
          };
        },
      ) => {
        state.warnings = state.warnings?.map((item: WarningGroup) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map(
            (warning: { node: TransactionWarning }) => {
              let resultWarning = { ...warning };

              resultWarning = {
                node: {
                  ...resultWarning.node,
                  action: action.meta.arg.originalArgs.body
                    .action as TransactionWarningActionEnum,
                  comment: action.meta.arg.originalArgs.body.comment,
                  matchStatus: action.meta.arg.originalArgs.body.matchStatus,
                },
              };

              return resultWarning;
            },
          );

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrdersBeneficiaries
        .matchFulfilled,
      (state, action: PayloadAction<Response<Order>>) => {
        const nextOrders = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.CASH_COLLECTION_ORDER, {
            transactionId: item.node.paymentOrderId as string,
          }),
          orderStatus: item?.node?.paymentOrder?.status,
          orderCreatedAt: item?.node?.paymentOrder?.orderCreatedAt,
          orderAcceptedAt: item?.node?.paymentOrder?.orderAcceptedAt,
          recepientAccountId: item?.node?.paymentOrder?.recepientAccountId,
        }));

        state.cashCollectionOrders = nextOrders;

        state.cashCollectionOrdersPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      cashCollectionListApi.endpoints.getPaymentsOrdersBeneficiary
        .matchFulfilled,
      (state, action: PayloadAction<{ data: Order }>) => {
        state.cashCollectionOrder = action.payload.data;
      },
    );
  },
});

export const {
  setTakeCount,
  clearPaymentOrderState,
  clearPaymentOrdersList,
  clearPaymentOrdersBeneficiariesList,
  clearCashCollectionOrder,
} = cashCollectionListSlice.actions;

export default cashCollectionListSlice.reducer;
